import { useContext, useMemo } from 'react';

import type { VersionContextValue } from '@core/context';
import { VersionContext } from '@core/context';
import { useSuperHubStore } from '@core/store';

interface Args {
  /**
   * For legacy projects, `'original'` will return the original unprocessed
   * version name and `'clean'` will return the clean processed version name.
   * For example, with version `2.0` the original value will be `2.0` and the
   * clean value will be `2.0.0`,
   *
   * ⚠️ For SuperHub projects `'clean'` names are not available and this will
   * always return the original name.
   *
   * @default 'original'
   */
  kind?: 'clean' | 'original';
}

/**
 * This hook replaces most uses of `VersionContext`. Because that context
 * does not support branches, relying on it can incorrectly return the parent
 * version name rather than the branch name when viewing a branch. For example,
 * when viewing `v2.0_my-feature-branch` in SuperHub, this:
 *
 * ```jsx
 * const { version } = useContext(VersionContext)
 * ```
 *
 * will return `2.0` rather than `2.0_my-feature-branch`. Because that `version`
 * const is often used to construct API request URLs, this can cause bugs where
 * content read or actions made on a branch actually get performed on the parent
 * version. This is at a minimum misleading if we display the wrong content to
 * the user, but at most can be dangerous if we unexpectedly save staged content
 * on a branch to its live parent version instead.
 *
 * ⚠️ So, `VersionContext` should only be used in legacy project components
 * which FOR SURE do not need to support branches on SuperHub and ALWAYS want to
 * refer to the current or parent version. For SuperHub components which always
 * want the current or parent version, see `superHubStore.getCurrentVersion()`.
 *
 * @summary A wrapper around `VersionContext` and the SuperHub store, to
 * provide a safe way to get either the current version or branch name in
 * both legacy and SuperHub projects.
 *
 * @returns If the current project is a SuperHub project, returns the name of
 * the current branch which may either be a version or a feature branch. If the
 * current project is a legacy project, returns the name of the current version
 * since branching is not a supported feature in legacy.
 */
export default function useLegacyCompatibleBranchName({ kind = 'original' }: Args = {}) {
  // This is one valid use of VersionContext as a wrapper around its
  // functionality. Other consumers should usually use this hook instead of
  // accessing `VersionContext` directly.
  // eslint-disable-next-line readme-internal/no-use-version-context
  const { version: versionName, version_clean: versionNameClean } = useContext(VersionContext) as VersionContextValue;
  const [isSuperHub, branchName] = useSuperHubStore(s => [s.isSuperHub, s.currentBranch.name]);

  return useMemo(() => {
    if (isSuperHub) {
      // SuperHub branch names can contain special characters (e.g. "/") that
      // need to be encoded for use in API request and page URLs. SuperHub-only
      // components that rely on `apiBaseUrl` don't need to worry about this,
      // because we encode the branch component of the URL automatically when we
      // initialize that property in the store.
      return encodeURIComponent(branchName);
    }
    return kind === 'original' ? versionName : versionNameClean;
  }, [branchName, isSuperHub, kind, versionName, versionNameClean]);
}
